import { useRouter } from "next/router";
import type { FunctionComponent } from "react";
import { useEffect } from "react";
import { graphql, useQueryLoader } from "react-relay";

import { ClientSideSuspense } from "shared/components/common/client-side-suspense";
import { notEmpty } from "shared/lib/utils";

import { DefaultPageSize } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { sanitizeKeyword } from "scmp-app/components/search/helpers";
import type {
  ArticleSearchAnswerModeEnum,
  searchResultPanelQuery,
} from "scmp-app/queries/__generated__/searchResultPanelQuery.graphql";

import { SearchResultList } from "./result-list";
import { StyledLoading } from "./styles";

export const query = graphql`
  query searchResultPanelQuery(
    $after: String
    $articleTypeIds: [String!]
    $contentTypes: [ContentTypeEnum!]
    $endDate: Timestamp
    $first: Int = 10
    $mode: ArticleSearchAnswerModeEnum
    $orderBy: ArticlesSearchOrderByInput
    $paywallTypeIds: [String!]
    $query: String!
    $sectionIds: [String!]
    $startDate: Timestamp
    $streamId: String!
    $variant: Int
  ) {
    ...resultListQuery
      @arguments(
        after: $after
        articleTypeIds: $articleTypeIds
        contentTypes: $contentTypes
        endDate: $endDate
        first: $first
        mode: $mode
        orderBy: $orderBy
        paywallTypeIds: $paywallTypeIds
        query: $query
        sectionIds: $sectionIds
        startDate: $startDate
        streamId: $streamId
        variant: $variant
      )
  }
`;

type Props = {
  answerMode?: ArticleSearchAnswerModeEnum;
  className?: string;
  noResultList?: boolean;
  streamId: string;
};

export const SearchResultPanel: FunctionComponent<Props> = ({
  answerMode,
  className,
  noResultList = false,
  streamId,
}) => {
  const { query: routerQuery } = useRouter();
  const { articleResultIdMap, currentValue, refineSearch } = useSearchContext();
  const [queryReference, loadQuery, disposeQuery] = useQueryLoader<searchResultPanelQuery>(query);

  useEffect(() => {
    if (answerMode === undefined) return;
    if (!notEmpty(currentValue)) return;
    if (sanitizeKeyword(routerQuery.q as string) !== currentValue) return;

    articleResultIdMap?.clear();

    loadQuery(
      {
        articleTypeIds: refineSearch.articleTypeIds,
        contentTypes: refineSearch.contentTypes,
        first: DefaultPageSize,
        mode: answerMode,
        orderBy: refineSearch.orderBy,
        paywallTypeIds: refineSearch.paywallTypeIds,
        query: currentValue,
        sectionIds: refineSearch.sectionIds,
        streamId,
        variant: 1,
        ...refineSearch.dateRange,
      },
      { fetchPolicy: "network-only" },
    );

    return () => {
      disposeQuery();
    };
  }, [
    answerMode,
    articleResultIdMap,
    currentValue,
    disposeQuery,
    loadQuery,
    refineSearch.articleTypeIds,
    refineSearch.contentTypes,
    refineSearch.dateRange,
    refineSearch.orderBy,
    refineSearch.paywallTypeIds,
    refineSearch.sectionIds,
    routerQuery.q,
    streamId,
  ]);

  if (noResultList || !notEmpty(currentValue)) return null;

  return (
    <ClientSideSuspense fallback={<StyledLoading />}>
      {queryReference && <SearchResultList className={className} reference={queryReference} />}
    </ClientSideSuspense>
  );
};

SearchResultPanel.displayName = "SearchResultPanel";
